import React from 'react';
import {
  Container, Box, Typography, Grid, Card, CardContent, Paper,
  Button, Chip, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  CheckCircle, Cancel, ExpandMore, Security, Gavel, Shield,
  ReportProblem, EventAvailable, WorkOutline, School, Description,
  Schedule, AttachMoney
} from '@mui/icons-material';
import photos from '../data/photos';
import SEO from './SEO';

const APPLY_URL = 'https://cyberwisecorp.com/apply';

// ---------------------------------------------------------------------------
// Styled components (same patterns as main site LandingPage.js)
// ---------------------------------------------------------------------------

const HeroSection = styled(Box)({
  backgroundImage: `linear-gradient(rgba(30,58,138,0.82), rgba(124,58,237,0.82)), url("${photos.hero}")`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  color: 'white',
  padding: '80px 0 100px',
  textAlign: 'center',
});

const ValueCard = styled(Card)({
  height: '100%',
  textAlign: 'center',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
  },
});

const StepNumber = styled(Box)({
  width: 60,
  height: 60,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1.5rem',
  fontWeight: 'bold',
  margin: '0 auto 16px',
  boxShadow: '0 4px 15px rgba(124, 58, 237, 0.3)',
});

const StepCard = styled(Paper)({
  padding: '32px 24px',
  textAlign: 'center',
  height: '100%',
  borderRadius: 16,
});

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

const whatYouGetCards = [
  {
    title: 'Real Client Work',
    photo: photos.realWork,
    desc: 'Not lab exercises. Not CTFs. Actual customers, actual networks, actual problems. The kind of experience hiring managers ask about in interviews.',
  },
  {
    title: 'CISSP Feedback After Every Job',
    photo: photos.cisspFeedback,
    desc: 'Every report you submit gets reviewed by a senior security professional. You will learn faster in 10 assessments than in a semester of coursework, because the feedback is specific to your work.',
  },
  {
    title: 'Portfolio Artifacts',
    photo: photos.portfolio,
    desc: 'Sanitized versions of your reports become resume material. "I have personally completed and delivered 23 cybersecurity assessments to real clients" is a sentence almost no other applicant can write.',
  },
  {
    title: 'Schedule Control',
    photo: photos.schedule,
    desc: 'You take jobs when you want them. No shifts, no quotas, no minimum hours. Midterms week? Take zero jobs. Summer break? Take ten a week.',
  },
];

const applyIfItems = [
  'Currently enrolled in a cybersecurity, computer science, IT, or related degree program at one of our partner schools (GCU, ASU, NAU, UOA, UOP, UAT, more coming)',
  'Completed at least one networking course and one security course (or equivalent self-study you can demonstrate)',
  'Comfortable walking into a stranger\'s home or small business and being the technical authority in the room for an hour',
  'Able to get to job sites in your area (car, transit, or rideshare — your choice)',
  'Can pass a standard background check (we pay for it)',
  'Can communicate clearly in writing because your reports are read by paying customers',
];

const dontApplyIfItems = [
  'Looking for guaranteed weekly hours or a steady paycheck. This is gig work, not employment.',
  'Expecting us to teach you cybersecurity from scratch. We provide the standardized checklist and the expert review, but you need the foundational knowledge to use them.',
  'Not comfortable being independently accountable for your work and your schedule.',
];

const legalItems = [
  { label: 'Independent contractor', text: 'You are a 1099 contractor, not an employee. You are responsible for your own taxes. We recommend setting aside about 25% of every payment.', icon: <Gavel /> },
  { label: 'Background check required', text: 'We pay for it. Standard criminal and identity check, same as any employer would run.', icon: <Shield /> },
  { label: 'Insurance is in place', text: 'CWC carries general liability, professional liability (E&O), and cyber liability insurance that covers students performing assessments under our standardized process.', icon: <Security /> },
  { label: 'You follow the standardized checklist', text: 'This is non-negotiable. The checklist exists to protect you, the customer, and the company.', icon: <CheckCircle /> },
  { label: 'Incident reporting is mandatory', text: 'If anything unexpected happens during an assessment, you stop work and call our incident line. Always.', icon: <ReportProblem /> },
  { label: 'You can decline any job, any time, for any reason', text: 'This is genuinely your choice. We do not penalize you for declining.', icon: <EventAvailable /> },
];

const applicationSteps = [
  { number: 1, title: '5-Minute Application', desc: 'Name, school, year, courses completed, why you are interested. We read every one.' },
  { number: 2, title: 'Background Check', desc: 'We pay for it. Takes 3-5 business days.' },
  { number: 3, title: '3-5 Minute Video Submission', desc: 'Record a short video answering three questions: Why are you interested in cybersecurity? What area of cybersecurity interests you most? What do you hope to gain from this program? We review every submission.' },
  { number: 4, title: 'Onboarding Training & Certification Test', desc: 'Watch our onboarding training video, then complete a 40-question test covering our checklist, platform, incident reporting, and contractor agreement. You must score 80% or higher to be cleared for field work.' },
  { number: 5, title: 'First Assessment with Phone Support', desc: 'Your first real job is yours to run. A CWC mentor is available by phone the entire time if you have questions or hit something unexpected. You get paid for it. After this, you are cleared to take jobs independently.' },
];

const faqItems = [
  {
    q: 'Is this an internship or a job?',
    a: 'Neither, technically. You are an independent contractor (1099). It is closer to driving for Uber than to a traditional internship, except the work is actual cybersecurity, and you get expert review and feedback after every job.',
  },
  {
    q: 'Will this count for my school\'s internship or practicum credit?',
    a: 'We are working with program directors at our partner schools to formalize this. Ask your program advisor and tell them you have applied to Cyber Wise Corp. They may already have information from us.',
  },
  {
    q: 'Do I need a car?',
    a: 'No. At $140 per assessment, an Uber or Lyft ride to and from the job still leaves you with strong earnings per hour. We dispatch jobs based on geography, so your fares will usually be short. Whatever you are currently doing to get to work or class will work here too, and this is likely more profitable than your current option.',
  },
  {
    q: 'What if I damage something at a customer\'s site?',
    a: 'Follow the standardized checklist. The checklist is non-invasive and does not modify customer systems. As long as you follow the checklist, our insurance covers incidental issues. If you go off-script and break something, the insurance may not cover it.',
  },
  {
    q: 'What happens if a customer is hostile or makes me feel unsafe?',
    a: 'You leave. Immediately. You call our incident line on the way out. You will be paid for the time you spent on-site. The customer will be removed from our platform if appropriate. Your safety is non-negotiable.',
  },
  {
    q: 'Can I do this if I\'m an international student on an F-1 visa?',
    a: 'This depends on your school\'s CPT/OPT rules. Independent contractor work generally requires CPT authorization tied to your major. Talk to your school\'s international student office before you apply. We cannot give immigration advice and we cannot sponsor visas.',
  },
  {
    q: 'How do you handle taxes?',
    a: 'You are a 1099 contractor. We send you a 1099-NEC at the end of the year if you have earned over $600. You are responsible for your own federal, state, and self-employment taxes. We recommend setting aside about 25% of each payment.',
  },
  {
    q: 'What schools do you work with?',
    a: 'Currently Grand Canyon University, Arizona State University, Northern Arizona University, University of Arizona, University of Phoenix, and University of Advancing Technology. We are expanding. If your school is not listed, you can still apply. We add schools based on student interest and customer demand in that geography.',
  },
  {
    q: 'Who reviews my reports?',
    a: 'A CISSP-certified senior security professional with 10+ years of field experience. Every report. No exceptions.',
  },
  {
    q: 'What if I want to stop?',
    a: 'You can stop at any time. No notice period, no penalty. This is gig work. If it is not for you, that is completely fine.',
  },
];

// ---------------------------------------------------------------------------
// Gold CTA button style (reused across sections)
// ---------------------------------------------------------------------------
const goldButtonSx = {
  backgroundColor: '#fbbf24',
  color: '#1e1e1e',
  boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
  border: '2px solid #fbbf24',
  px: 5,
  py: 1.5,
  fontSize: '1.2rem',
  fontWeight: 700,
  '&:hover': {
    backgroundColor: '#f59e0b',
    boxShadow: '0 6px 25px rgba(0,0,0,0.5)',
    border: '2px solid #f59e0b',
  },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function StudentPage() {
  return (
    <Box>
      <SEO description="Launch your cybersecurity career with real-world experience. Work on real client assessments, get CISSP expert feedback, and build a professional portfolio. Now recruiting in Arizona." />

      {/* ================================================================= */}
      {/* GREEN BANNER                                                       */}
      {/* ================================================================= */}
      <Box sx={{ background: 'linear-gradient(90deg, #16a34a, #059669)', color: 'white', py: 1.5, textAlign: 'center' }}>
        <Container maxWidth="lg">
          <Typography variant="body1" sx={{ fontWeight: 700, fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
            Now recruiting at GCU, ASU, NAU, UOA, UOP, and UAT. Apply in 5 minutes.
          </Typography>
        </Container>
      </Box>

      {/* ================================================================= */}
      {/* HERO SECTION                                                       */}
      {/* ================================================================= */}
      <HeroSection>
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip label="Launch Your Career"
            sx={{ mb: 3, backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600 }} />
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Real Cybersecurity Experience. Before You Graduate.
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.95, maxWidth: 750, mx: 'auto', mb: 4, lineHeight: 1.6 }}>
            Work on real client assessments. Get expert feedback from CISSP-certified professionals. Build a portfolio that employers actually care about. No coffee runs. No fake internships. Competitive pay.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" size="large"
              component="a" href={APPLY_URL} target="_blank" rel="noopener noreferrer"
              sx={goldButtonSx}>
              Apply Now
            </Button>
            <Button variant="outlined" size="large"
              onClick={() => document.getElementById('what-you-do')?.scrollIntoView({ behavior: 'smooth' })}
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', px: 4, py: 1.5, fontSize: '1.1rem', fontWeight: 600,
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}>
              See How It Works
            </Button>
          </Box>
        </Container>
      </HeroSection>

      {/* ================================================================= */}
      {/* SECTION 1: THE EXPERIENCE PROBLEM (white)                          */}
      {/* ================================================================= */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
          You Need Experience to Get Hired. You Need to Get Hired to Get Experience.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mt: 3 }}>
          Every cybersecurity job listing wants 2-3 years of hands-on experience for an "entry level" role. The internships that would give you that experience take a few hundred students nationally and reject thousands. If you did not land one, your options are usually:
        </Typography>
        <Box sx={{ mt: 3, ml: 2 }}>
          {[
            'An unpaid "internship" doing helpdesk work that has nothing to do with security',
            'Setting up a home lab and hoping a Hack The Box badge counts as experience',
            'Waiting until graduation and competing against people who already have real-world hours',
          ].map((item, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
              <Cancel sx={{ color: 'error.main', fontSize: 20, mt: 0.3 }} />
              <Typography variant="body1" color="text.secondary">{item}</Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="body1" sx={{ mt: 4, fontWeight: 600, lineHeight: 1.8 }}>
          We built Cyber Wise Corp because there is a better option: real, paid, supervised security work that you can do on your own schedule, near your campus, starting now.
        </Typography>
      </Container>

      {/* ================================================================= */}
      {/* SECTION 2: WHAT YOU ACTUALLY DO (gray)                             */}
      {/* ================================================================= */}
      <Box id="what-you-do" sx={{ backgroundColor: 'grey.50', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
            The Work, in Plain English
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mt: 3 }}>
            A homeowner or small business books an assessment on our site. We dispatch the job to a vetted student near them. You show up at the agreed time, spend one hour walking through their network, devices, IoT gear, and digital security setup using our standardized checklist. You give them a 15-minute walkthrough of what you found. You upload your findings to our platform.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mt: 2 }}>
            A CISSP-certified expert reviews your report within 48 hours, adds any context you missed, and the customer gets the final report. You get written feedback on what you caught, what you missed, and how to do it better next time.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, mt: 2 }}>
            You get paid $140 per completed and approved assessment. Most assessments take about 90 minutes total when you count drive time, the on-site hour, and the report write-up.
          </Typography>
          <Paper elevation={0} sx={{ p: 3, mt: 4, backgroundColor: 'rgba(124,58,237,0.05)', borderRadius: 3, border: '1px solid rgba(124,58,237,0.2)' }}>
            <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.8 }}>
              Each assessment becomes a portfolio artifact. A real client deliverable, with a real expert review, that you can show in interviews. After 10 assessments, you have done more hands-on field work than most students do in an entire degree program.
            </Typography>
          </Paper>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
            Work on real cybersecurity assessments for homes and businesses.{' '}
            <Box component="a" href="https://cyberwisecorp.com/home-assessment" target="_blank" rel="noopener noreferrer"
              sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'underline' }}>
              See our home assessment
            </Box>
            {' and '}
            <Box component="a" href="https://cyberwisecorp.com/business-assessment" target="_blank" rel="noopener noreferrer"
              sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'underline' }}>
              business assessment
            </Box>
            {' services.'}
          </Typography>
        </Container>
      </Box>

      {/* ================================================================= */}
      {/* TRUST BAR                                                          */}
      {/* ================================================================= */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 5, borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} justifyContent="center">
            {[
              { photo: photos.trustClients, text: 'Real paying clients across Arizona' },
              { photo: photos.trustReview, text: 'Every assessment reviewed by a CISSP-certified expert' },
              { photo: photos.trustMethodology, text: 'Structured methodology with real deliverables' },
              { photo: photos.trustInsured, text: 'Insured, supervised, and professionally managed' },
            ].map((item, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, textAlign: 'center' }}>
                  <Box component="img" src={item.photo} alt={item.text}
                    sx={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.15)' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.text}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ================================================================= */}
      {/* SECTION 3: WHAT YOU GET (white)                                    */}
      {/* ================================================================= */}
      <Box id="what-you-get" sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
            What You Get Besides Money
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {whatYouGetCards.map((card, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <ValueCard>
                  <Box sx={{ width: '100%', height: 180, overflow: 'hidden' }}>
                    <Box component="img" src={card.photo} alt={card.title}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>{card.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{card.desc}</Typography>
                  </CardContent>
                </ValueCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ================================================================= */}
      {/* SECTION 4: WHO WE'RE LOOKING FOR (gray)                            */}
      {/* ================================================================= */}
      <Box id="requirements" sx={{ backgroundColor: 'grey.50', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
            The Honest Requirements
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'success.main' }}>You Should Apply If:</Typography>
              {applyIfItems.map((item, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                  <CheckCircle sx={{ color: 'success.main', fontSize: 20, mt: 0.3, flexShrink: 0 }} />
                  <Typography variant="body2">{item}</Typography>
                </Box>
              ))}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'error.main' }}>You Should NOT Apply If:</Typography>
              {dontApplyIfItems.map((item, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                  <Cancel sx={{ color: 'error.main', fontSize: 20, mt: 0.3, flexShrink: 0 }} />
                  <Typography variant="body2">{item}</Typography>
                </Box>
              ))}
            </Grid>
          </Grid>
          <Typography variant="body1" sx={{ mt: 4, fontWeight: 600, textAlign: 'center' }}>
            We say no to a lot of applicants. Not because they are not smart, but because this work is not right for everyone. We would rather tell you that now than waste your time and ours.
          </Typography>
        </Container>
      </Box>

      {/* ================================================================= */}
      {/* SECTION 5: LEGAL AND SAFETY (white)                                */}
      {/* ================================================================= */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
          Things You Should Know Before Applying
        </Typography>
        <Box sx={{ mt: 4 }}>
          {legalItems.map((item, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
              <Box sx={{ color: '#7c3aed', mt: 0.3, flexShrink: 0 }}>{item.icon}</Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{item.label}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{item.text}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>

      {/* ================================================================= */}
      {/* SECTION 6: HOW MUCH YOU CAN MAKE (gray)                            */}
      {/* ================================================================= */}
      <Box id="earnings" sx={{ backgroundColor: 'grey.50', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
            Real Numbers, Not Marketing Math
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <Paper elevation={2} sx={{ p: 4, textAlign: 'center', borderRadius: 3, height: '100%', border: '2px solid', borderColor: 'primary.light' }}>
                <School sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>School Year (Part-Time)</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>$280<Typography component="span" variant="h6" color="text.secondary">/week</Typography></Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2, lineHeight: 1.7 }}>
                  2 assessments per week x $140 = $280/week
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  Roughly $1,120/month during the academic year
                </Typography>
                <Chip label="~$10,000 over 9 months" sx={{ mt: 2, fontWeight: 600, backgroundColor: 'primary.main', color: 'white' }} />
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper elevation={2} sx={{ p: 4, textAlign: 'center', borderRadius: 3, height: '100%', border: '2px solid', borderColor: 'secondary.main' }}>
                <AttachMoney sx={{ fontSize: 48, color: 'secondary.main', mb: 1 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Summer (Full-Time)</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'secondary.main' }}>$700<Typography component="span" variant="h6" color="text.secondary">/week</Typography></Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2, lineHeight: 1.7 }}>
                  5 assessments per week x $140 = $700/week
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  Over 12 weeks: approximately $8,400
                </Typography>
                <Chip label="~$8,400 over summer" sx={{ mt: 2, fontWeight: 600, backgroundColor: 'secondary.main', color: 'white' }} />
              </Paper>
            </Grid>
          </Grid>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 4, textAlign: 'center', lineHeight: 1.7, fontStyle: 'italic' }}>
            We are not promising these numbers. Your actual earnings depend on customer demand in your area, how many jobs you accept, and how quickly your reports clear expert review. Some students will earn more, some will earn less, and some will quit after their second assessment because the work is not for them. We would rather you know that going in.
          </Typography>
        </Container>
      </Box>

      {/* ================================================================= */}
      {/* SECTION 7: APPLICATION PROCESS (white)                             */}
      {/* ================================================================= */}
      <Box id="application-process" sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
            What Happens After You Click Apply
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {applicationSteps.map((step) => (
              <Grid item xs={12} sm={6} md={step.number <= 3 ? 4 : 6} key={step.number}>
                <StepCard elevation={1}>
                  <StepNumber>{step.number}</StepNumber>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>{step.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{step.desc}</Typography>
                </StepCard>
              </Grid>
            ))}
          </Grid>
          <Typography variant="body1" sx={{ mt: 4, fontWeight: 600, textAlign: 'center' }}>
            Total time from application to first paid assessment: typically 2-3 weeks.
          </Typography>
        </Container>
      </Box>

      {/* ================================================================= */}
      {/* CTA SECTION (gradient)                                             */}
      {/* ================================================================= */}
      <Box sx={{
        backgroundImage: `linear-gradient(rgba(30,58,138,0.85), rgba(124,58,237,0.85)), url("${photos.cta}")`,
        backgroundSize: 'cover', backgroundPosition: 'center', py: 8,
      }}>
        <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" color="white" gutterBottom sx={{ fontWeight: 700 }}>
            Ready?
          </Typography>
          <Typography variant="h6" color="white" sx={{ mb: 4, opacity: 0.95 }}>
            Questions? Use our contact form or join our student Discord.
          </Typography>
          <Button variant="contained" size="large"
            component="a" href={APPLY_URL} target="_blank" rel="noopener noreferrer"
            sx={goldButtonSx}>
            Apply Now
          </Button>
        </Container>
      </Box>

      {/* ================================================================= */}
      {/* FAQ ACCORDION (gray)                                               */}
      {/* ================================================================= */}
      <Box id="faq" sx={{ backgroundColor: 'grey.50', py: 8 }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
            Frequently Asked Questions
          </Typography>
          <Box sx={{ mt: 4 }}>
            {faqItems.map((item, i) => (
              <Accordion key={i} sx={{ mb: 1, borderRadius: '8px !important', '&:before': { display: 'none' } }}
                elevation={1}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{item.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>{item.a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default StudentPage;
